/**
 * Pendant zu den `localStorage`-Zugriffen, die der Altclient über `IJSRuntime` ausführt
 * (`MainLayout.LoadSidebarState` / `SaveSidebarState`).
 *
 * Wie dort gilt: Ein leerer oder unlesbarer Speicherstand wird ignoriert und der
 * Anfangswert übernommen; Fehler beim Schreiben brechen den Ablauf nicht ab.
 */

import { useCallback, useState } from 'react';

export interface LocalStorageCodec<T> {
  /** Wandelt den gespeicherten Text; wirft die Funktion, gilt der Anfangswert. */
  parse?: (raw: string) => T;
  /** Erzeugt den zu speichernden Text; Standard `JSON.stringify`. */
  serialize?: (value: T) => string;
}

export function useLocalStorage<T>(
  key: string,
  initial: T,
  codec: LocalStorageCodec<T> = {},
): [T, (value: T) => void] {
  const { parse, serialize } = codec;

  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      // Pendant zu `string.IsNullOrEmpty(storedState)`.
      if (raw === null || raw === '') {
        return initial;
      }
      return parse === undefined ? (JSON.parse(raw) as T) : parse(raw);
    } catch {
      return initial;
    }
  });

  const store = useCallback(
    (next: T): void => {
      setValue(next);
      try {
        const raw = serialize === undefined ? JSON.stringify(next) : serialize(next);
        window.localStorage.setItem(key, raw);
      } catch {
        // Der Altclient protokolliert den Fehler nur auf der Konsole und arbeitet weiter.
      }
    },
    [key, serialize],
  );

  return [value, store];
}
